import * as dateFns from 'date-fns';
import { timestamp, db } from 'src/utils/firebase';
import { useEventLogger } from 'src/contexts/event-context';
import { flattenObject } from '../plaid/forecast/utils';
import { useFinanceContext } from '../plaid/datasource';

export function useOnboardingService(userId) {
  const { assetService, scenarioService } = useFinanceContext();
  const { updateScenario } = scenarioService;
  const { saveAssets } = assetService;
  const { logEvent } = useEventLogger();

  // save all form data to each fields respective locations
  async function save(formData) {
    await saveBaseCase(makeAssumptions(formData));
    await saveProfile(makeProfile(formData));
    await saveAssets(makeAssets(formData));
    await logEvent('completeSetup');
  }

  // we'll update the default assumptions
  async function saveBaseCase(data) {
    await updateScenario('default', data);
  }

  // helper to assemble form data into assumptions
  function makeAssumptions(formData) {
    const data = flattenObject(formData);
    const result = {};

    // retirement
    if (data?.targetRetirementAge) result.targetRetirementAge = Number(data?.targetRetirementAge);

    // asset values
    if (data?.netHomeValue) result.propertyValue = Number(data?.netHomeValue);
    if (data?.privateAssetsValue) result.privateAssetsValue = Number(data?.privateAssetsValue);

    // asset growth rates
    if (data?.averageIncomeGrowthRate)
      result.averageIncomeGrowthRate = Number(data?.averageIncomeGrowthRate);

    // average private asset growth rate
    if (data?.averagePrivateAssetGrowthRate)
      result.averagePrivateAssetGrowthRate = Number(data?.averagePrivateAssetGrowthRate);

    // average real estate growth rate
    if (data?.averageRealEstateGrowthRate)
      result.averageRealEstateGrowthRate = Number(data?.averageRealEstateGrowthRate);

    // equities
    if (data?.averageEquityGrowthRate)
      result.averageEquityGrowthRate = Number(data?.averageEquityGrowthRate);
    if (data?.averageEquityWithdrawalRate)
      result.averageEquityWithdrawalRate = Number(data?.averageEquityWithdrawalRate);

    // taxes
    if (data?.taxRateRetention) result.taxRateRetention = Number(data?.taxRateRetention);

    // inflation
    if (data?.annualInflationRate) result.annualInflationRate = Number(data?.annualInflationRate);

    // retirement
    if (data?.averageRetirementEquityWithdrawalRate)
      result.averageRetirementEquityWithdrawalRate = Number(
        data?.averageRetirementEquityWithdrawalRate
      );
    if (data?.monthlySocialSecurityIncome)
      result.socialSecurityIncome = Number(data?.monthlySocialSecurityIncome);

    return result;
  }

  // update the users profile with new data
  async function saveProfile(data) {
    try {
      const userRef = db.collection('users').doc(userId);
      const userDoc = await userRef.get();
      if (userDoc.exists) {
        await userRef.update(data, { merge: true });
      } else {
        await userRef.set(data);
      }
      // await updateScenario('default', data);
    } catch (error) {
      console.error('Error saving profile', error);
      throw error;
    }
  }

  // helper to assemble form data into a profile
  function makeProfile(formData) {
    const result = { ...formData?.profile, onboarded: true };

    // save the string to a firestore timestamp for demographic querying
    if (typeof result?.dob === 'string') result.dob = timestamp(dateFns.parseISO(result?.dob));

    return result;
  }

  // real estate and private assets
  function makeAssets(formData) {
    const sanitize = (data = []) =>
      data
        .filter((e) => !!e.value)
        .map((e) => ({
          ...e,
          name: e.name ?? e.placeholder,
          value: Number(e.value),
          provider: 'manual',
        }));

    const sanitizedData = {
      real_estate: sanitize(formData?.finances?.houses).map((e) => ({
        ...e,
        type: 'real_estate',
        subtype: 'real_estate',
      })),
      private_assets: sanitize(formData?.finances?.assets).map((e) => ({
        ...e,
        type: 'private_asset',
        subtype: 'other',
      })),
    };

    return Object.values(sanitizedData).flat();
  }

  async function preSaveProfile(formData) {
    await saveProfile(makeProfile(formData));
  }

  return { preSaveProfile, save };
}
